import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'

import { App } from './App'
import { LanguageProvider } from './i18n/language'
// Polices auto-hébergées (aucun CDN externe, fonctionne hors ligne) — identité du canevas v0.5.0 :
// Fraunces (titres), IBM Plex Sans (texte), IBM Plex Mono (identifiants). Sous-ensemble latin
// (couvre le français, Œ/œ compris) ; licences SIL OFL 1.1 dans node_modules/@fontsource/*/LICENSE.
import '@fontsource/ibm-plex-sans/latin-400.css'
import '@fontsource/ibm-plex-sans/latin-500.css'
import '@fontsource/ibm-plex-sans/latin-600.css'
import '@fontsource/ibm-plex-sans/latin-700.css'
import '@fontsource/ibm-plex-mono/latin-400.css'
import '@fontsource/ibm-plex-mono/latin-500.css'
import '@fontsource/ibm-plex-mono/latin-600.css'
import '@fontsource/fraunces/latin-500.css'
import '@fontsource/fraunces/latin-600.css'
// tokens.css (palette AA + thème) d'abord, puis theme.css (Tailwind + shadcn) qui
// importe app.css dans la couche `components`.
import './styles/tokens.css'
import './styles/theme.css'
import { mutationCache } from './lib/mutation-feedback'
import { armerPurgeCacheSurChangementIdentite } from './lib/query-cache-identity-guard'
import { initTheme } from './lib/theme'
import { initDensity } from './lib/density'
import { initTechnical, TechnicalProvider } from './lib/technical'
import { armerPontAriaHiddenInert } from './lib/ariaHiddenInert'
import { capturerJetonInvitation } from './lib/lienInvitation'

// Applique le thème, la densité et le mode « détails techniques » avant le premier rendu.
// FND-V050-050 : un lien d'invitation (`#jeton=`) est recueilli AVANT la connexion OIDC, qui perd le fragment.
capturerJetonInvitation()
initTheme()
initDensity()
initTechnical()
// v030-menu-utilisateur-aria-hidden-focus-a-l-ouverture : synchronise `inert` sur tout
// élément que Radix marque aria-hidden="true" à l'ouverture d'un overlay modal (menu,
// dialogue…) — Radix ne le fait jamais lui-même, laissant ces éléments focusables au
// clavier malgré aria-hidden (violation axe aria-hidden-focus).
armerPontAriaHiddenInert()

// Retries bornés : une erreur d'autorisation ou de ressource absente ne doit jamais
// être réessayée — cela masquerait le vrai problème derrière une attente.
const queryClient = new QueryClient({
  // Le retour d'une action réussie est décidé une fois pour toutes ici : sans ce cache, 74
  // mutations sur 78 ne confirmaient rien du tout. Voir `lib/mutation-feedback.ts`.
  mutationCache,
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status ?? 0
        if (status >= 400 && status < 500) return false
        return failureCount < 2
      },
      staleTime: 30_000,
    },
  },
})

// v030-eng-perf-001 : sans ceci, un changement d'identité (déconnexion/reconnexion dans
// le même onglet, aucune des deux ne recharge la page) laisse le cache React Query
// servir les données du compte/organisation PRÉCÉDENT jusqu'à staleTime (30s ci-dessus).
armerPurgeCacheSurChangementIdentite(queryClient)

const container = document.getElementById('root')
if (!container) throw new Error('élément racine introuvable')

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TechnicalProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </TechnicalProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </StrictMode>,
)
